import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/context/AuthContext.jsx';
import useLanguageNavigation from '@/hooks/useLanguageNavigation.js';
import { getChipSrc } from '@/utils/chips.js';
import { loadReplayArchive } from '@/features/recording/replayTransport.js';
import { createReplaySession } from '@/features/recording/replaySession.js';
import { createReplayAudioClock } from '@/features/recording/replayAudioClock.js';
import './RecordingArchivePage.css';

const LABELS = {
  ru: {
    back: 'К списку игр', title: 'Записи игровой сессии', archive: 'Архив записей',
    loading: 'Загружаем запись…', noRecords: 'Для этой игры записей пока нет.',
    open: 'Смотреть', started: 'Начало', expired: 'Хранится до',
    sound: 'Звук', mute: 'Выключить звук', unmute: 'Включить звук',
    play: 'Воспроизвести', pause: 'Пауза', speed: 'Скорость', back10: 'Назад на 10 секунд',
    forward10: 'Вперёд на 10 секунд', board: 'Игровое поле',
    players: 'Участники', active: 'Ход', paused: 'Пауза',
    speakers: 'Сейчас говорят', none: 'Нет активных говорящих', dice: 'Кубик',
    cards: 'Открытые карточки', noCards: 'Карточки закрыты', history: 'История событий',
    older: 'Показать предыдущие события', noEvents: 'Событий ещё нет',
    move: 'Ход', undo: 'Отмена хода', roll: 'Бросок', openCard: 'Открытие карточки',
    closeCard: 'Закрытие карточки', pauseEvent: 'Пауза игрока',
    resume: 'Возобновление игрока', turn: 'Передача хода',
    finished: 'Запись завершена', recorded: 'Запись начата',
    voice: 'Разрешение голоса', cell: 'Клетка', close: 'Закрыть',
    failed: 'Не удалось загрузить запись', retry: 'Повторить',
    unavailable: 'Запись недоступна или срок её хранения истёк.',
    audioError: 'Часть звука недоступна. Игровые события воспроизводятся без изменений.',
    imageError: 'Некоторые изображения архива недоступны. Игровые события и тексты карточек сохранены.',
    conductor: 'Проводник',
    exportTitle: 'Экспорт MP4', exportButton: 'Создать MP4',
    exportStarted: 'Экспорт поставлен в очередь', exportWorking: 'Готовится видео…',
    exportDownload: 'Скачать MP4', exportReady: 'Видео готово',
    exportExpiry: 'Скачать до', exportFailed: 'Не удалось создать MP4',
    exportNotAllowed: 'Дождитесь завершения записи',
    exportError: 'Ошибка экспорта. Повторите запрос.',
    exportTooLarge: 'Для больших файлов используйте Chrome или Edge.',
    exportSaveCancelled: 'Сохранение отменено',
    shareTitle: 'Приватные ссылки на просмотр',
    shareButton: 'Создать ссылку', shareOne: '1 день', shareThree: '3 дня', shareSeven: '7 дней',
    shareNotice: 'Любой, у кого есть ссылка, сможет смотреть игру и разрешённый к записи голос. Доступ можно отозвать.',
    shareCopy: 'Копировать ссылку', shareCopied: 'Скопировано', shareCopyManual: 'Выделите и скопируйте ссылку',
    shareNew: 'Новая ссылка (сохраните сейчас: позже токен недоступен)',
    shareCreated: 'Создана', shareExpires: 'Действует до',
    shareRevoked: 'Отозвана', shareRevoke: 'Отозвать', shareEmpty: 'Активных ссылок нет',
    shareError: 'Не удалось выполнить действие со ссылкой',
    shareView: 'Приватная запись игровой сессии',
    deleteRecording: 'Удалить запись', deleteConfirm: 'Удалить запись навсегда? Видео, аудио и все ссылки станут недоступны.',
    deleteFailed: 'Не удалось удалить запись. Если создаётся MP4, дождитесь завершения экспорта.',
    audioGap: 'На этом участке нет звуковой дорожки',
    recording: 'Запись идёт', done: 'Завершена', seek: 'Перемотка', fullScreen: 'На весь экран',
    archiveBack: 'Все записи', readOnly: 'Просмотр записи · изменения игры отключены',
  },
  ua: {
    back: 'До списку ігор', title: 'Записи ігрової сесії', archive: 'Архів записів',
    loading: 'Завантажуємо запис…', noRecords: 'Для цієї гри записів поки немає.',
    open: 'Переглянути', started: 'Початок', expired: 'Зберігається до',
    sound: 'Звук', mute: 'Вимкнути звук', unmute: 'Увімкнути звук',
    play: 'Відтворити', pause: 'Пауза', speed: 'Швидкість', back10: 'Назад на 10 секунд',
    forward10: 'Вперед на 10 секунд', board: 'Ігрове поле',
    players: 'Учасники', active: 'Хід', paused: 'Пауза',
    speakers: 'Зараз говорять', none: 'Немає активних мовців', dice: 'Кубик',
    cards: 'Відкриті картки', noCards: 'Картки закриті', history: 'Історія подій',
    older: 'Показати попередні події', noEvents: 'Подій ще немає',
    move: 'Хід', undo: 'Скасування ходу', roll: 'Кидок', openCard: 'Відкриття картки',
    closeCard: 'Закриття картки', pauseEvent: 'Пауза гравця',
    resume: 'Відновлення гравця', turn: 'Передача ходу',
    finished: 'Запис завершено', recorded: 'Запис розпочато',
    voice: 'Дозвіл голосу', cell: 'Клітинка', close: 'Закрити',
    failed: 'Не вдалося завантажити запис', retry: 'Повторити',
    unavailable: 'Запис недоступний або термін зберігання минув.',
    audioError: 'Частина звуку недоступна. Ігрові події відтворюються без змін.',
    imageError: 'Деякі зображення архіву недоступні. Ігрові події та тексти карток збережено.',
    conductor: 'Провідник',
    exportTitle: 'Експорт MP4', exportButton: 'Створити MP4',
    exportStarted: 'Експорт у черзі', exportWorking: 'Готується відео…',
    exportDownload: 'Завантажити MP4', exportReady: 'Відео готове',
    exportExpiry: 'Завантажити до', exportFailed: 'Не вдалося створити MP4',
    exportNotAllowed: 'Дочекайтесь завершення запису',
    exportError: 'Помилка експорту. Повторіть запит.',
    exportTooLarge: 'Для великих файлів використовуйте Chrome або Edge.',
    exportSaveCancelled: 'Збереження скасовано',
    shareTitle: 'Приватні посилання для перегляду',
    shareButton: 'Створити посилання', shareOne: '1 день', shareThree: '3 дні', shareSeven: '7 днів',
    shareNotice: 'Кожен, хто має посилання, може переглянути гру і дозволені до запису голоси. Доступ можна відкликати.',
    shareCopy: 'Копіювати', shareCopied: 'Скопійовано', shareCopyManual: 'Виділіть і скопіюйте посилання',
    shareNew: 'Нове посилання (збережіть зараз: згодом токен недоступний)',
    shareCreated: 'Створено', shareExpires: 'Діє до',
    shareRevoked: 'Відкликано', shareRevoke: 'Відкликати', shareEmpty: 'Активних посилань немає',
    shareError: 'Не вдалося виконати дію з посиланням',
    shareView: 'Приватний запис ігрової сесії',
    deleteRecording: 'Видалити запис', deleteConfirm: 'Видалити запис назавжди? Відео, аудіо і всі посилання стануть недоступними.',
    deleteFailed: 'Не вдалося видалити запис. Якщо створюється MP4, дочекайтесь завершення експорту.',
    audioGap: 'На цій ділянці немає звукової доріжки',
    recording: 'Триває запис', done: 'Завершено', seek: 'Перемотування', fullScreen: 'На весь екран',
    archiveBack: 'Усі записи', readOnly: 'Перегляд запису · зміни гри вимкнено',
  },
  en: {
    back: 'Back to games', title: 'Game session recordings', archive: 'Recording archive',
    loading: 'Loading recording…', noRecords: 'No recordings for this game yet.',
    open: 'Watch', started: 'Started', expired: 'Available until',
    sound: 'Audio', mute: 'Mute audio', unmute: 'Unmute audio',
    play: 'Play', pause: 'Pause', speed: 'Speed', back10: 'Back 10 seconds',
    forward10: 'Forward 10 seconds', board: 'Game board',
    players: 'Players', active: 'Current turn', paused: 'Paused',
    speakers: 'Currently speaking', none: 'Nobody speaking', dice: 'Dice',
    cards: 'Open cards', noCards: 'All cards closed', history: 'Event history',
    older: 'Show earlier events', noEvents: 'No events yet',
    move: 'Move', undo: 'Move undone', roll: 'Roll', openCard: 'Card opened',
    closeCard: 'Card closed', pauseEvent: 'Player paused',
    resume: 'Player resumed', turn: 'Turn changed',
    finished: 'Recording finished', recorded: 'Recording started',
    voice: 'Voice permission', cell: 'Cell', close: 'Close',
    failed: 'Could not load recording', retry: 'Retry',
    unavailable: 'Recording unavailable or retention period has expired.',
    audioError: 'Some audio is unavailable. The game timeline continues unchanged.',
    imageError: 'Some archived images are unavailable. Game events and card text remain available.',
    conductor: 'Conductor',
    exportTitle: 'Export MP4', exportButton: 'Create MP4',
    exportStarted: 'Export queued', exportWorking: 'Preparing video…',
    exportDownload: 'Download MP4', exportReady: 'Video is ready',
    exportExpiry: 'Download before', exportFailed: 'Could not create MP4',
    exportNotAllowed: 'Wait until recording is complete',
    exportError: 'Export failed. Try again.',
    exportTooLarge: 'Use Chrome or Edge for large downloads.',
    exportSaveCancelled: 'Saving cancelled',
    shareTitle: 'Private playback links',
    shareButton: 'Create link', shareOne: '1 day', shareThree: '3 days', shareSeven: '7 days',
    shareNotice: 'Anyone with the link can watch the game and recorded consenting voices. You can revoke access.',
    shareCopy: 'Copy link', shareCopied: 'Copied', shareCopyManual: 'Select and copy the link',
    shareNew: 'New link (save it now: the token cannot be recovered later)',
    shareCreated: 'Created', shareExpires: 'Expires',
    shareRevoked: 'Revoked', shareRevoke: 'Revoke', shareEmpty: 'No active links',
    shareError: 'Could not manage the sharing link',
    shareView: 'Private game session replay',
    deleteRecording: 'Delete recording', deleteConfirm: 'Permanently delete recording? Video, audio and all links will stop working.',
    deleteFailed: 'Cannot delete recording. If MP4 export is running, wait until it finishes.',
    audioGap: 'No audio on this part of the timeline',
    recording: 'Recording', done: 'Completed', seek: 'Seek', fullScreen: 'Full screen',
    archiveBack: 'All recordings', readOnly: 'Recording playback · game controls are disabled',
  },
};

const EVENTS = {
  recording_started: 'recorded', recording_stopped: 'finished',
  recording_resumed: 'recorded', voice_consent_requested: 'voice',
  dice_rolled: 'roll', move_applied: 'move', move_undone: 'undo',
  card_opened: 'openCard', card_closed: 'closeCard', player_paused: 'pauseEvent',
  player_resumed: 'resume', turn_changed: 'turn', voice_consent_changed: 'voice',
};
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const BOARD_COLUMNS = 9;
const BOARD_ROWS = 8;
const formatTime = (ms) => {
  const seconds = Math.floor(Math.max(0, ms) / 1000);
  return String(Math.floor(seconds / 3600)).padStart(2, '0') + ':' +
    String(Math.floor((seconds % 3600) / 60)).padStart(2, '0') + ':' +
    String(seconds % 60).padStart(2, '0');
};
const dateText = (value, language) => value
  ? new Date(value).toLocaleString(language === 'ua' ? 'uk-UA' : language === 'ru' ? 'ru-RU' : 'en-US')
  : '—';

const cellPosition = (number) => {
  const cell = Math.min(72, Math.max(1, Number(number) || 1)) - 1;
  const row = Math.floor(cell / BOARD_COLUMNS);
  const col = cell % BOARD_COLUMNS;
  return {
    left: ((row % 2 === 0 ? col : BOARD_COLUMNS - col - 1) / BOARD_COLUMNS) * 100 + '%',
    top: ((BOARD_ROWS - row - 1) / BOARD_ROWS) * 100 + '%',
    width: 100 / BOARD_COLUMNS + '%',
    height: 100 / BOARD_ROWS + '%',
  };
};
const resolveTitles = (assets, snapshot) => {
  const config = snapshot?.game?.fieldConfig || assets?.fieldConfig || {};
  const titles = config.boardConfig?.cellTitles || config.cellTitles || {};
  const cells = Array.isArray(config.cells) ? config.cells : [];
  const result = { ...titles };
  cells.forEach((cell) => {
    if (Number.isInteger(cell?.number)) result[cell.number] = cell.title || '';
  });
  return result;
};
const playerName = (players, id) => players[id]?.name || String(id || '—');
const getDice = (value) => {
  if (typeof value === 'number') return value;
  return value?.value ?? value?.diceValue ?? value?.roll ?? '—';
};

function ReplayBoard({ data, replay, labels, boardUrl, visualUrls, ensureAsset, playing }) {
  const players = Object.values(replay.players);
  const titles = useMemo(() => resolveTitles(data.bundle.assets, data.bundle.snapshot), [data]);
  const [selectedCell, setSelectedCell] = useState(null);
  const card = selectedCell ? replay.cardMap[selectedCell] : null;
  const atCell = useMemo(() => {
    const grouped = {};
    for (const player of players) {
      const cell = Math.min(72, Math.max(1, Number(player.currentCell) || 1));
      (grouped[cell] ||= []).push(player);
    }
    return grouped;
  }, [replay.players]);

  return (
    <section className="replay-panel replay-panel--board" aria-label={labels.board}>
      <div className="replay-panel__header">
        <h2>{labels.board}</h2>
        <span className={'replay-dice' + (replay.lastEvent?.kind === 'dice_rolled' ? ' is-rolling' : '')}>🎲 {labels.dice}: <strong>{getDice(replay.dice)}</strong></span>
      </div>
      <div className="replay-board" style={boardUrl ? { backgroundImage: 'url("' + boardUrl + '")' } : {}}>
        {Array.from({ length: 72 }, (_, index) => {
          const cell = index + 1;
          return (
            <button type="button" key={cell} className="replay-board__cell"
              style={cellPosition(cell)} onClick={() => { setSelectedCell(cell); ensureAsset('card-' + cell); }}
              title={labels.cell + ' ' + cell + (titles[cell] ? ': ' + titles[cell] : '')}>
              <span className="replay-board__number">{cell}</span>
              {titles[cell] && <span className="replay-board__title">{titles[cell]}</span>}

            </button>
          );
        })}
        <div className={'replay-pieces' + (playing ? ' is-playing' : '')} aria-hidden="true">
          {players.map((player) => {
            const position = cellPosition(player.currentCell);
            const occupants = atCell[player.currentCell] || [player];
            const offset = occupants.findIndex((entry) => entry.id === player.id);
            const left = parseFloat(position.left) + 50 / BOARD_COLUMNS +
              (offset - (occupants.length - 1) / 2) * 2.5;
            const top = parseFloat(position.top) + 50 / BOARD_ROWS;
            return (
              <span key={player.id}
                className={'replay-piece' +
                  (player.id === replay.session.currentPlayerId ? ' is-current' : '')}
                style={{ left: left + '%', top: top + '%' }} title={player.name}>
                {visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)
                  ? <img src={visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)} alt="" />
                  : <span>●</span>}
              </span>
            );
          })}
        </div>
      </div>
      <p className="replay-muted">{labels.readOnly}</p>
      {selectedCell !== null && (
        <div className="replay-card-backdrop" role="presentation" onClick={() => setSelectedCell(null)}>
          <div className="replay-card-modal" role="dialog" aria-modal="true"
            aria-label={labels.cell + ' ' + selectedCell} onClick={(event) => event.stopPropagation()}>
            <button type="button" className="replay-card-modal__close" onClick={() => setSelectedCell(null)}>
              {labels.close} ×
            </button>
            <span>{labels.cell} {selectedCell}</span>
            <h3>{card?.title || titles[selectedCell] || '—'}</h3>
            {visualUrls['card-' + selectedCell] && (
              <img className="replay-card-modal__image"
                src={visualUrls['card-' + selectedCell]} alt={card?.title || ''} />
            )}
            {!card?.imageUrl && (
              <p className="replay-card-modal__body"
                style={{
                  ...(visualUrls['card-background']
                    ? { backgroundImage: 'url("' + visualUrls['card-background'] + '")' } : {}),
                  ...(data.bundle.assets?.deckSnapshot?.textColor
                    ? { color: data.bundle.assets.deckSnapshot.textColor } : {}),
                }}>{card?.text || '—'}</p>
            )}
            {card?.imageUrl && !visualUrls['card-' + selectedCell] && (
              <p className="replay-muted">{card?.title || '—'}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

const speakerNames = (speakers, consent, players, labels) =>
  speakers.filter((identity) => consent[identity] !== false)
    .map((identity) => identity.startsWith('p_')
      ? playerName(players, Number(identity.slice(2)))
      : identity.startsWith('u_') ? labels.conductor : identity);

function ReplaySidePanel({ data, frame, labels, olderCount, setOlderCount, onSeek, visualUrls }) {
  const { replay } = frame;
  const events = data.bundle.events;
  const lastIndex = useMemo(() => {
    let lo = 0; let hi = events.length - 1; let result = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (events[mid].atMs <= frame.timeMs) { result = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    return result;
  }, [events, frame.timeMs]);
  const visible = events.slice(Math.max(0, lastIndex + 1 - olderCount), lastIndex + 1).reverse();
  const speaking = speakerNames(replay.speakers, replay.consent, replay.players, labels);
  return (
    <div className="replay-side">
      <section className="replay-panel">
        <h2>{labels.players}</h2>
        <ul className="replay-player-list">
          {Object.values(replay.players).sort((a, b) => (a.turnOrder || a.id) - (b.turnOrder || b.id))
            .map((player) => (
              <li key={player.id} className={player.id === replay.session.currentPlayerId ? 'is-current' : ''}>
                {(visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)) &&
                  <img src={visualUrls['chip-' + player.chipKey] || getChipSrc(player.chipKey)} alt="" />}
                <span><strong>{player.name}</strong><small>{labels.cell} {player.currentCell || 1}</small></span>
                {player.isPaused && <em>{labels.paused}</em>}
                {player.id === replay.session.currentPlayerId && <em>{labels.active}</em>}
              </li>
            ))}
        </ul>
        <h3>{labels.speakers}</h3>
        <p className="replay-muted" aria-live="polite">{speaking.join(', ') || labels.none}</p>
      </section>
      <section className="replay-panel">
        <h2>{labels.cards}</h2>
        {Object.entries(replay.openedCards).length === 0
          ? <p className="replay-muted">{labels.noCards}</p>
          : <div className="replay-open-cards">
              {Object.entries(replay.openedCards).map(([actor, entry]) => (
                <article key={actor}>
                  <small>{labels.cell} {entry.cellNumber}</small>
                  <h3>{entry.card?.title || '—'}</h3>
                  {visualUrls['card-' + entry.cellNumber]
                    ? <img className="replay-open-card-image"
                        src={visualUrls['card-' + entry.cellNumber]} alt={entry.card?.title || ''} />
                    : <p>{entry.card?.text || '—'}</p>}
                </article>
              ))}
            </div>}
      </section>
      <section className="replay-panel">
        <h2>{labels.history}</h2>
        <ol className="replay-history">
          {visible.map((event) => (
            <li key={event.sequence}>
              <button type="button" onClick={() => onSeek(event.atMs)}>
                <time>{formatTime(event.atMs)}</time>
                <span>{labels[EVENTS[event.kind]] || event.kind}</span>
                {event.kind === 'move_applied' &&
                  <small>{playerName(replay.players, event.payload.playerId)}: {event.payload.fromCell} → {event.payload.toCell}</small>}
              </button>
            </li>
          ))}
        </ol>
        {lastIndex < 0 && <p className="replay-muted">{labels.noEvents}</p>}
        {lastIndex + 1 > olderCount && (
          <button type="button" className="replay-secondary" onClick={() => setOlderCount((n) => n + 50)}>
            {labels.older}
          </button>
        )}
      </section>
    </div>
  );
}

export default function RecordingArchivePage({
  shared = false, libraryScope = false, gameType = 'group',
}) {
  const { id: gameId, recordingId } = useParams();
  const location = useLocation();
  // The capability token is held only in the URL fragment; no server URL or
  // referrer includes this secret.
  const shareToken = shared ? location.hash.replace(/^#/, '') : undefined;
  const { token } = useAuth();
  const { i18n } = useTranslation();
  const { getLocalizedPath } = useLanguageNavigation();
  const language = i18n.language?.startsWith('ru') ? 'ru' : i18n.language?.startsWith('en') ? 'en' : 'ua';
  const labels = LABELS[language];
  const [records, setRecords] = useState([]);
  const [data, setData] = useState(null);
  const [frame, setFrame] = useState(null);
  const [olderCount, setOlderCount] = useState(50);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [audioError, setAudioError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [muted, setMuted] = useState(false);
  const [reload, setReload] = useState(0);
  const [exportJob, setExportJob] = useState(null);
  const [exportError, setExportError] = useState('');
  const [exportBusy, setExportBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareDays, setShareDays] = useState(1);
  const [shareUrl, setShareUrl] = useState('');
  const [shareCreatedId, setShareCreatedId] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [deleteBusyId, setDeleteBusyId] = useState('');
  const [archiveActionError, setArchiveActionError] = useState('');
  const playerRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const [visualUrls, setVisualUrls] = useState({});
  const visualUrlsRef = useRef({});
  const assetRequestsRef = useRef(new Set());
  const assetAbortRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    setAudioError(false);
    setImageError(false);
    setExportError('');
    setExportJob(null);
    setShareError('');
    setShareUrl('');
    setShareCreatedId('');
    setShareCopied(false);
    setData(null);
    setFrame(null);
    setOlderCount(50);
    assetAbortRef.current?.abort();
    assetAbortRef.current = new AbortController();
    Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    visualUrlsRef.current = {};
    assetRequestsRef.current.clear();
    setVisualUrls({});
    sessionRef.current?.dispose();
    sessionRef.current = null;
    audioRef.current = null;
    const load = async () => {
      try {
        if (!shared && !token) throw new Error('AUTH_REQUIRED');
        if (!recordingId && !shared) {
          const response = await fetch(apiUrl('/api/games/' + gameId + '/recordings'), {
            signal: controller.signal, cache: 'no-store',
            headers: { Authorization: 'Bearer ' + token },
          });
          if (!response.ok) throw new Error('ARCHIVE_REQUEST_FAILED');
          const result = await response.json();
          if (!cancelled) setRecords(Array.isArray(result.recordings) ? result.recordings : []);
          return;
        }
        const archive = await loadReplayArchive({
          gameId, recordingId, token, shareToken, libraryScope, apiUrl, signal: controller.signal,
        });
        if (cancelled) return;
        const audioClock = archive.audioTimeline.length ? createReplayAudioClock({
          timeline: archive.audioTimeline,
          loadBlob: archive.transport.audioBlob,
          onError: () => { if (!cancelled) setAudioError(true); },
        }) : null;
        if (audioClock) audioClock.element.muted = muted;
        audioRef.current = audioClock;
        const replaySession = createReplaySession({
          index: archive.index, audioClock,
          onUpdate: (next) => { if (!cancelled) setFrame(next); },
        });
        sessionRef.current = replaySession;
        setData(archive);
        if (archive.bundle.assets?.visualStatus === 'partial') setImageError(true);
        setFrame(replaySession.snapshot());
      } catch (error) {
        if (!cancelled && error?.name !== 'AbortError') {
          setLoadError(error?.status === 410 || error?.status === 404
            ? 'unavailable' : 'failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      controller.abort();
      sessionRef.current?.dispose();
      sessionRef.current = null;
      audioRef.current = null;
      assetAbortRef.current?.abort();
      Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
      visualUrlsRef.current = {};
      assetRequestsRef.current.clear();
    };
    // Mute is handled directly by the audio element, not by refetching the archive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, recordingId, token, shareToken, shared, libraryScope, reload]);

  useEffect(() => {
    if (!frame?.playing) return undefined;
    let animationFrame;
    const tick = () => {
      sessionRef.current?.tick();
      animationFrame = window.requestAnimationFrame(tick);
    };
    animationFrame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [frame?.playing]);

  const ensureAsset = useCallback(async (key) => {
    if (!data?.bundle?.assets?.visualFiles?.[key]
      || visualUrlsRef.current[key] || assetRequestsRef.current.has(key)) return;
    const controller = assetAbortRef.current;
    if (!controller || controller.signal.aborted) return;
    assetRequestsRef.current.add(key);
    try {
      const blob = await data.transport.visualAsset(key, controller.signal);
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      if (controller.signal.aborted) { URL.revokeObjectURL(url); return; }
      visualUrlsRef.current[key] = url;
      setVisualUrls((current) => ({ ...current, [key]: url }));
    } catch (error) {
      if (error?.name !== 'AbortError') setImageError(true);
    } finally {
      assetRequestsRef.current.delete(key);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      ensureAsset('board');
      ensureAsset('card-background');
      for (const player of data.bundle.snapshot.players) {
        if (player.chipKey) ensureAsset('chip-' + player.chipKey);
      }
    }
  }, [data, ensureAsset]);

  useEffect(() => {
    for (const item of Object.values(frame?.replay?.openedCards || {})) {
      if (item?.card?.imageUrl) ensureAsset('card-' + item.cellNumber);
    }
  }, [frame?.replay?.openedCards, ensureAsset]);

  // Revoking a link invalidates new requests immediately. An already-open
  // online viewer also loses their in-memory player within the next 15 seconds.
  // Previously downloaded bytes cannot be recalled from someone's device.
  useEffect(() => {
    if (!shared || !shareToken || !data) return undefined;
    let cancelled = false;
    const checkShare = async () => {
      try {
        const response = await fetch(apiUrl('/api/recording-shares'), {
          method: 'GET', cache: 'no-store', referrerPolicy: 'no-referrer',
          headers: { 'X-Recording-Share': shareToken },
        });
        if (!cancelled && [404, 410].includes(response.status)) {
          sessionRef.current?.dispose();
          sessionRef.current = null;
          assetAbortRef.current?.abort();
          Object.values(visualUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
          visualUrlsRef.current = {};
          setVisualUrls({});
          setFrame(null);
          setData(null);
          setLoadError('unavailable');
        }
      } catch {
        // Temporary connectivity failures are not authorization decisions.
      }
    };
    const timer = setInterval(checkShare, 15000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [shared, shareToken, Boolean(data)]);

  useEffect(() => {
    if (!data || shared || !['#replay-shares', '#replay-mp4'].includes(location.hash)) {
      return undefined;
    }
    const frameId = window.requestAnimationFrame(() => {
      document.getElementById(location.hash.slice(1))?.scrollIntoView({
        behavior: 'smooth', block: 'start',
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [data, shared, location.hash]);

  const recordingApiBase = libraryScope
    ? '/api/recording-library/archives/' + recordingId
    : '/api/games/' + gameId + '/recordings/' + recordingId;
  const exportBase = !shared && recordingId
    ? apiUrl(recordingApiBase + '/exports')
    : '';
  const exportRequest = useCallback(async (url, options = {}) => {
    const response = await fetch(url, {
      ...options, cache: 'no-store',
      headers: { Authorization: 'Bearer ' + token, ...(options.headers || {}) },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.code || 'EXPORT_REQUEST_FAILED');
    }
    return response;
  }, [token]);

  useEffect(() => {
    if (!exportBase || !token) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const response = await exportRequest(exportBase);
        const result = await response.json();
        if (!cancelled) setExportJob(result.exports?.[0] || null);
      } catch (error) {
        if (!cancelled) setExportError(labels.exportError);
      }
    };
    refresh();
    return () => { cancelled = true; };
  }, [exportBase, token, exportRequest, labels.exportError]);

  useEffect(() => {
    if (!exportBase || !exportJob || !['queued', 'processing'].includes(exportJob.status)) return undefined;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const response = await exportRequest(exportBase + '/' + exportJob.id);
        const result = await response.json();
        if (!cancelled) setExportJob(result.export);
      } catch (error) {
        if (!cancelled) setExportError(labels.exportError);
      }
    }, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [exportBase, exportJob?.id, exportJob?.status, exportRequest, labels.exportError]);

  const startMp4Export = async () => {
    if (!exportBase || exportBusy) return;
    setExportBusy(true);
    setExportError('');
    try {
      const response = await exportRequest(exportBase, { method: 'POST' });
      const data = await response.json();
      setExportJob(data.export);
    } catch (error) {
      setExportError(labels.exportError + ' (' + (error.message || 'error') + ')');
    } finally {
      setExportBusy(false);
    }
  };

  const downloadMp4 = async () => {
    if (!exportJob || exportJob.status !== 'completed' || downloadBusy) return;
    setDownloadBusy(true);
    setExportError('');
    let writable = null;
    try {
      // Picker must be opened synchronously from the user's click.
      const handle = typeof window.showSaveFilePicker === 'function'
        ? await window.showSaveFilePicker({
          suggestedName: 'leela-recording.mp4',
          types: [{ description: 'MP4 Video', accept: { 'video/mp4': ['.mp4'] } }],
        }) : null;
      const response = await exportRequest(exportBase + '/' + exportJob.id + '/file');
      if (handle && response.body) {
        writable = await handle.createWritable();
        const reader = response.body.getReader();
        try {
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            await writable.write(value);
          }
          await writable.close();
          writable = null;
        } finally {
          reader.releaseLock();
        }
      } else {
        const size = Number(response.headers.get('content-length') || exportJob.byteSize || 0);
        if (size > 512 * 1024 * 1024) throw new Error('LARGE_DOWNLOAD');
        const blob = await response.blob();
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = 'leela-recording.mp4';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(href), 60000);
      }
    } catch (error) {
      if (writable) await writable.abort().catch(() => {});
      if (error?.name !== 'AbortError') {
        setExportError(error.message === 'LARGE_DOWNLOAD' ? labels.exportTooLarge : labels.exportError);
      }
    } finally {
      setDownloadBusy(false);
    }
  };

  const sharesBase = !shared && recordingId
    ? apiUrl(recordingApiBase + '/shares')
    : '';

  useEffect(() => {
    if (!sharesBase || !token) return undefined;
    let cancelled = false;
    exportRequest(sharesBase).then((response) => response.json())
      .then((result) => {
        if (!cancelled) setShareLinks(Array.isArray(result.shares) ? result.shares : []);
      })
      .catch(() => { if (!cancelled) setShareError(labels.shareError); });
    return () => { cancelled = true; };
  }, [sharesBase, token, exportRequest, labels.shareError]);

  const createPrivateShare = async () => {
    if (!sharesBase || shareBusy) return;
    setShareBusy(true);
    setShareError('');
    setShareCopied(false);
    try {
      const response = await exportRequest(sharesBase, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days: Number(shareDays) }),
      });
      const result = await response.json();
      setShareLinks((old) => [result.link, ...old]);
      setShareCreatedId(result.link.id);
      setShareUrl(new URL(getLocalizedPath('/recording-share') +
        '#' + result.token, window.location.origin).href);
    } catch (error) {
      setShareError(labels.shareError + ' (' + (error.message || 'error') + ')');
    } finally { setShareBusy(false); }
  };

  const copyPrivateShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
    } catch {
      setShareError(labels.shareCopyManual);
    }
  };

  const revokePrivateShare = async (linkId) => {
    if (!sharesBase || shareBusy) return;
    setShareBusy(true);
    setShareError('');
    try {
      await exportRequest(sharesBase + '/' + linkId, { method: 'DELETE' });
      setShareLinks((old) => old.map((link) =>
        link.id === linkId ? { ...link, revokedAt: new Date().toISOString() } : link));
      if (linkId === shareCreatedId) {
        setShareCreatedId('');
        setShareUrl('');
      }
    } catch (error) {
      setShareError(labels.shareError + ' (' + (error.message || 'error') + ')');
    } finally { setShareBusy(false); }
  };
  const activeShares = shareLinks.filter((link) => !link.revokedAt
    && new Date(link.expiresAt).getTime() > Date.now());

  const deleteArchive = async (targetId) => {
    if (deleteBusyId || !window.confirm(labels.deleteConfirm)) return;
    setDeleteBusyId(targetId);
    setArchiveActionError('');
    try {
      await exportRequest(
        apiUrl(libraryScope ? '/api/recording-library/archives/' + targetId
          : '/api/games/' + gameId + '/recordings/' + targetId),
        { method: 'DELETE' },
      );
      setRecords((existing) => existing.filter((item) => item.id !== targetId));
    } catch (error) {
      setArchiveActionError(labels.deleteFailed + ' (' + (error.message || 'error') + ')');
    } finally {
      setDeleteBusyId('');
    }
  };

  const seek = useCallback((ms) => sessionRef.current?.seek(ms), []);
  const toggleAudio = () => {
    const next = !muted;
    setMuted(next);
    if (audioRef.current?.element) audioRef.current.element.muted = next;
  };
  const archivePath = libraryScope ? '/dashboard/recordings'
    : '/dashboard/games/' + gameType + '/' + gameId + '/recordings';
  const boardUrl = visualUrls.board || '';
  const currentTimeMs = frame?.timeMs || 0;
  const durationMs = frame?.durationMs || 0;
  const hasAudio = Boolean(data?.audioTimeline?.length);

  return (
    <div className={'replay-page' + (data ? ' replay-page--viewer' : '')}>
      <header className="replay-header">
        <div>
          <p className="page-caption">{labels.archive}</p>
          <h1>{data?.bundle?.snapshot?.game?.title || labels.title}</h1>
        </div>
        {!shared && (
          <Link className="replay-secondary" to={getLocalizedPath(recordingId ? archivePath : '/dashboard/games/group?tab=finished')}>
            ← {recordingId ? labels.archiveBack : labels.back}
          </Link>
        )}
      </header>
      {loading && <div className="replay-panel" role="status">{labels.loading}</div>}
      {!!loadError && (
        <div className="replay-panel replay-error" role="alert">
          <p>{labels[loadError]}</p>
          <button type="button" className="replay-secondary" onClick={() => setReload((n) => n + 1)}>{labels.retry}</button>
        </div>
      )}
      {!loading && !loadError && !recordingId && !shared && (
        <section className="replay-panel">
          <h2>{labels.archive}</h2>
          {!!archiveActionError && <p role="alert" className="replay-audio-warning">{archiveActionError}</p>}
          {records.length === 0 && <p className="replay-muted">{labels.noRecords}</p>}
          <div className="replay-recordings">
            {records.map((recording) => (
              <article key={recording.id} className="replay-record">
                <div>
                  <strong>{labels.started}: {dateText(recording.recordingStartedAt, language)}</strong>
                  <p className="replay-muted">{recording.status === 'completed' ? labels.done : labels.recording}
                    {' · '}{labels.expired}: {dateText(recording.expiresAt, language)}</p>
                </div>
                <div className="replay-record-actions">
                  <Link className="replay-primary" to={getLocalizedPath(archivePath + '/' + recording.id)}>
                    {labels.open}
                  </Link>
                  {recording.status === 'completed' && (
                    <button type="button" className="replay-secondary"
                      disabled={Boolean(deleteBusyId)} onClick={() => deleteArchive(recording.id)}>
                      {labels.deleteRecording}
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      {data && frame && !loading && !loadError && (
        <>
          {!shared && data.bundle.recording.status === 'completed' && (
            <section id="replay-shares" className="replay-share-panel" aria-label={labels.shareTitle}>
              <div>
                <h2>{labels.shareTitle}</h2>
                <p className="replay-muted">{labels.shareNotice}</p>
              </div>
              <div className="replay-share-actions">
                <select aria-label={labels.shareExpires} value={shareDays}
                  onChange={(event) => setShareDays(Number(event.target.value))}>
                  <option value={1}>{labels.shareOne}</option>
                  <option value={3}>{labels.shareThree}</option>
                  <option value={7}>{labels.shareSeven}</option>
                </select>
                <button type="button" className="replay-primary"
                  disabled={shareBusy || activeShares.length >= 5}
                  onClick={createPrivateShare}>{labels.shareButton}</button>
              </div>
              {shareUrl && (
                <div className="replay-share-created">
                  <label htmlFor="replay-share-new">{labels.shareNew}</label>
                  <div className="replay-share-link">
                    <input id="replay-share-new" type="text" value={shareUrl}
                      readOnly onFocus={(event) => event.target.select()} />
                    <button type="button" className="replay-secondary"
                      onClick={copyPrivateShare}>{shareCopied ? labels.shareCopied : labels.shareCopy}</button>
                  </div>
                </div>
              )}
              {!!shareError && <p role="alert" className="replay-audio-warning">{shareError}</p>}
              {activeShares.length === 0 && <p className="replay-muted">{labels.shareEmpty}</p>}
              {activeShares.length > 0 && (
                <ul className="replay-share-list">
                  {activeShares.map((link) => (
                    <li key={link.id}>
                      <span>{labels.shareCreated}: {dateText(link.createdAt, language)}
                        {' · '}{labels.shareExpires}: {dateText(link.expiresAt, language)}</span>
                      <button type="button" className="replay-secondary" disabled={shareBusy}
                        onClick={() => revokePrivateShare(link.id)}>{labels.shareRevoke}</button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
          {!shared && <section id="replay-mp4" className="replay-export-panel" aria-label={labels.exportTitle}>
            <div>
              <strong>{labels.exportTitle}</strong>
              {exportJob?.status === 'completed' && (
                <p className="replay-muted">{labels.exportReady} · {labels.exportExpiry}: {dateText(exportJob.expiresAt, language)}</p>
              )}
              {['queued', 'processing'].includes(exportJob?.status) && (
                <p className="replay-muted" role="status">{exportJob.status === 'queued'
                  ? labels.exportStarted : labels.exportWorking}</p>
              )}
              {exportJob?.status === 'failed' && (
                <p className="replay-audio-warning">{labels.exportFailed} ({exportJob.errorCode || 'EXPORT_RENDER_FAILED'})</p>
              )}
              {exportError && <p className="replay-audio-warning" role="alert">{exportError}</p>}
              {data.bundle.recording.status !== 'completed' && (
                <p className="replay-muted">{labels.exportNotAllowed}</p>
              )}
            </div>
            {exportJob?.status === 'completed' ? (
              <button type="button" className="replay-primary"
                disabled={downloadBusy} onClick={downloadMp4}>{labels.exportDownload}</button>
            ) : (
              <button type="button" className="replay-primary"
                disabled={data.bundle.recording.status !== 'completed' || exportBusy ||
                  ['queued', 'processing'].includes(exportJob?.status)}
                onClick={startMp4Export}>{labels.exportButton}</button>
            )}
          </section>}
          <div className="replay-player" ref={playerRef}>
          <div className="replay-workspace">
            <ReplayBoard data={data} replay={frame.replay} labels={labels} boardUrl={boardUrl}
              visualUrls={visualUrls} ensureAsset={ensureAsset} playing={frame.playing} />
            <ReplaySidePanel data={data} frame={frame} labels={labels}
              olderCount={olderCount} setOlderCount={setOlderCount} onSeek={seek}
              visualUrls={visualUrls} />
          </div>          <section className="replay-toolbar" aria-label={labels.seek}>
            <div className="replay-toolbar__buttons">
              <button type="button" className="replay-primary"
                onClick={() => frame.playing ? sessionRef.current?.pause() : sessionRef.current?.play()}
                aria-label={frame.playing ? labels.pause : labels.play}>
                {frame.playing ? 'Ⅱ ' + labels.pause : '▶ ' + labels.play}
              </button>
              <button type="button" className="replay-secondary" onClick={() => seek(currentTimeMs - 10000)}
                aria-label={labels.back10}>↶ 10s</button>
              <button type="button" className="replay-secondary" onClick={() => seek(currentTimeMs + 10000)}
                aria-label={labels.forward10}>10s ↷</button>
              <span className="replay-time" aria-live="off">{formatTime(currentTimeMs)} / {formatTime(durationMs)}</span>
              <label className="replay-speed">{labels.speed}
                <select value={frame.speed} onChange={(event) => sessionRef.current?.setSpeed(Number(event.target.value))}>
                  {SPEEDS.map((speed) => <option key={speed} value={speed}>{speed}×</option>)}
                </select>
              </label>
              <button type="button" className="replay-secondary" disabled={!hasAudio}
                onClick={toggleAudio}>{muted ? labels.unmute : labels.mute}</button>
              <button type="button" className="replay-secondary replay-fullscreen-button"
                aria-label={labels.fullScreen} onClick={() => {
                  if (document.fullscreenElement) document.exitFullscreen?.();
                  else playerRef.current?.requestFullscreen?.();
                }}>{labels.fullScreen}</button>
            </div>
            <input type="range" className="replay-seek" min={0} max={Math.max(1, durationMs)}
              value={currentTimeMs} step={100} onChange={(event) => seek(Number(event.target.value))}
              aria-label={labels.seek} />
            {audioError && <p className="replay-audio-warning" role="status">{labels.audioError}</p>}
            {imageError && <p className="replay-audio-warning" role="status">{labels.imageError}</p>}
            {!hasAudio && <p className="replay-muted">{labels.audioGap}</p>}
          </section>
          </div>
        </>
      )}
    </div>
  );
}
